import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  CreateWorkYoutubeMatchPayload,
  CreateWorkYoutubeMatchResponse,
  WorkYoutubeMatch,
  WorkYoutubeMatchCollectionResponse,
  YoutubeMatchSubmitter,
} from '../../types/youtubeMatches';
import {
  createWorkYoutubeMatch,
  fetchWorkYoutubeMatches,
  YoutubeMatchesRequestError,
} from '../../services/youtubeMatches';
import {
  extractYoutubeVideoId,
  parseTimestampInput,
  parseYoutubeUrl,
  sanitizeNotes,
} from '../../utils/youtube';
import type { RootState } from '../store';
export type YoutubeMatchesStatus = 'idle' | 'loading' | 'loaded' | 'error';

type PendingSubmission = {
  tempId: number;
  submittedAt: string;
};

type YoutubeMatchesEntry = {
  matches: WorkYoutubeMatch[];
  total: number;
  status: YoutubeMatchesStatus;
  error?: string;
  pending: Record<string, PendingSubmission>;
  submitError?: string;
};

export type YoutubeMatchesState = {
  byWorkId: Record<string, YoutubeMatchesEntry>;
  nextTempId: number;
};

const initialState: YoutubeMatchesState = {
  byWorkId: {},
  nextTempId: -1,
};

const ensureEntry = (state: YoutubeMatchesState, workId: string): YoutubeMatchesEntry => {
  const existing = state.byWorkId[workId];
  if (existing) return existing;
  const next: YoutubeMatchesEntry = {
    matches: [],
    total: 0,
    status: 'idle',
    error: undefined,
    pending: {},
    submitError: undefined,
  };
  state.byWorkId[workId] = next;
  return next;
};

const parseDate = (value: string): number => {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortMatches = (matches: WorkYoutubeMatch[]): WorkYoutubeMatch[] => {
  return matches
    .slice()
    .sort((a, b) => {
      const createdDiff = parseDate(b.createdAt) - parseDate(a.createdAt);
      if (createdDiff !== 0) return createdDiff;
      return b.id - a.id;
    });
};

const mergeMatch = (matches: WorkYoutubeMatch[], incoming: WorkYoutubeMatch): WorkYoutubeMatch[] => {
  const next = matches.slice();
  const existingIndex = next.findIndex((match) => match.id === incoming.id);
  if (existingIndex >= 0) {
    next[existingIndex] = incoming;
  } else {
    next.push(incoming);
  }
  return sortMatches(next);
};

const removeMatchById = (matches: WorkYoutubeMatch[], id: number): WorkYoutubeMatch[] => {
  return matches.filter((match) => match.id !== id);
};

const coerceErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    const message = (error as any).message.trim();
    if (message.length > 0) return message;
  }
  return fallback;
};

type LoadYoutubeMatchesArgs = {
  workId: string;
  limit?: number;
};

type LoadYoutubeMatchesResult = {
  workId: string;
} & WorkYoutubeMatchCollectionResponse;

const DEFAULT_FETCH_ERROR = 'Unable to load YouTube matches.';

export const loadYoutubeMatches = createAsyncThunk<
  LoadYoutubeMatchesResult,
  LoadYoutubeMatchesArgs,
  { state: RootState; rejectValue: { workId: string; error: string } }
>(
  'youtubeMatches/load',
  async ({ workId, limit }, { rejectWithValue }) => {
    try {
      const data = await fetchWorkYoutubeMatches(workId, { limit });
      return { workId, ...data };
    } catch (error: unknown) {
      if (error instanceof YoutubeMatchesRequestError) {
        return rejectWithValue({ workId, error: error.message });
      }
      const fallback = error instanceof Error ? error.message : null;
      const message =
        fallback && fallback.trim().length ? fallback : DEFAULT_FETCH_ERROR;
      return rejectWithValue({ workId, error: message });
    }
  }
);

type SubmitYoutubeMatchArgs = {
  workId: string;
  payload: CreateWorkYoutubeMatchPayload;
  optimistic?: {
    submitter?: YoutubeMatchSubmitter;
  };
};

type SubmitYoutubeMatchResult = {
  workId: string;
} & CreateWorkYoutubeMatchResponse;

const DEFAULT_SUBMIT_ERROR = 'Unable to submit YouTube match.';

export const submitYoutubeMatch = createAsyncThunk<
  SubmitYoutubeMatchResult,
  SubmitYoutubeMatchArgs,
  { state: RootState; rejectValue: { workId: string; error: string } }
>(
  'youtubeMatches/submit',
  async ({ workId, payload }, { rejectWithValue }) => {
    try {
      const result = await createWorkYoutubeMatch(workId, payload);
      return { workId, ...result };
    } catch (error: unknown) {
      if (error instanceof YoutubeMatchesRequestError) {
        return rejectWithValue({ workId, error: error.message });
      }
      const fallback = error instanceof Error ? error.message : null;
      const message =
        fallback && fallback.trim().length ? fallback : DEFAULT_SUBMIT_ERROR;
      return rejectWithValue({ workId, error: message });
    }
  }
);

const youtubeMatchesSlice = createSlice({
  name: 'youtubeMatches',
  initialState,
  reducers: {
    clearYoutubeMatches(state, action: PayloadAction<{ workId: string }>) {
      const workId = action.payload.workId;
      if (!workId) return;
      delete state.byWorkId[workId];
    },
    resetSubmitError(state, action: PayloadAction<{ workId: string }>) {
      const entry = state.byWorkId[action.payload.workId];
      if (entry) {
        entry.submitError = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadYoutubeMatches.pending, (state, action) => {
      const { workId } = action.meta.arg;
      if (!workId) return;
      const entry = ensureEntry(state, workId);
      entry.status = 'loading';
      entry.error = undefined;
    });

    builder.addCase(loadYoutubeMatches.fulfilled, (state, action) => {
      const { workId, matches, total } = action.payload;
      const entry = ensureEntry(state, workId);
      entry.matches = sortMatches(matches);
      entry.total = total;
      entry.status = 'loaded';
      entry.error = undefined;
    });

    builder.addCase(loadYoutubeMatches.rejected, (state, action) => {
      const workId = action.payload?.workId ?? action.meta.arg.workId;
      if (!workId) return;
      const entry = ensureEntry(state, workId);
      entry.status = 'error';
      entry.error = coerceErrorMessage(action.payload?.error ?? action.error, DEFAULT_FETCH_ERROR);
    });

    builder.addCase(submitYoutubeMatch.pending, (state, action) => {
      const { workId, payload, optimistic } = action.meta.arg;
      if (!workId) return;
      const entry = ensureEntry(state, workId);
      entry.submitError = undefined;
      const tempId = state.nextTempId--;
      const now = new Date().toISOString();
      const submitter: YoutubeMatchSubmitter = optimistic?.submitter ?? {
        id: 'me',
        displayName: 'You',
        avatarUrl: null,
      };

      let inferredVideoId: string | null = null;
      let inferredStartMs: number | null = null;
      if (typeof payload.youtubeUrl === 'string' && payload.youtubeUrl.trim().length) {
        try {
          const parsed = parseYoutubeUrl(payload.youtubeUrl);
          inferredVideoId = parsed.videoId;
          inferredStartMs = parsed.startMsFromParam ?? null;
        } catch {
          inferredVideoId = null;
          inferredStartMs = null;
        }
      }

      let youtubeVideoId = '';
      if (typeof payload.youtubeVideoId === 'string' && payload.youtubeVideoId.trim().length) {
        try {
          youtubeVideoId = extractYoutubeVideoId(payload.youtubeVideoId);
        } catch {
          youtubeVideoId = payload.youtubeVideoId.trim();
        }
      } else if (inferredVideoId) {
        youtubeVideoId = inferredVideoId;
      }

      const resolveTimestamp = (
        value: string | number | null | undefined,
        field: 'start' | 'end',
        fallback: number | null
      ): number | null => {
        if (value === null || value === undefined || value === '') {
          return fallback;
        }
        try {
          const parsed = parseTimestampInput(value, field);
          if (parsed === null) {
            return fallback;
          }
          return parsed;
        } catch {
          return fallback;
        }
      };

      const startMs = resolveTimestamp(payload.start, 'start', inferredStartMs ?? 0) ?? 0;
      let endMs = resolveTimestamp(payload.end, 'end', null);
      if (endMs !== null && endMs < startMs) {
        endMs = null;
      }

      const notes = sanitizeNotes(payload.notes);

      const optimisticMatch: WorkYoutubeMatch = {
        id: tempId,
        workId,
        youtubeVideoId,
        performerCredit: payload.performerCredit,
        startMs,
        endMs,
        notes,
        createdAt: now,
        submitter,
        isMine: true,
      };

      entry.pending[action.meta.requestId] = { tempId, submittedAt: now };
      entry.matches = mergeMatch(entry.matches, optimisticMatch);
    });

    builder.addCase(submitYoutubeMatch.fulfilled, (state, action) => {
      const { workId, match, created } = action.payload;
      const entry = ensureEntry(state, workId);
      const pending = entry.pending[action.meta.requestId];
      if (pending) {
        entry.matches = removeMatchById(entry.matches, pending.tempId);
        delete entry.pending[action.meta.requestId];
      }

      entry.matches = mergeMatch(entry.matches, match);
      if (created) {
        entry.total += 1;
      }
    });

    builder.addCase(submitYoutubeMatch.rejected, (state, action) => {
      const workId = action.payload?.workId ?? action.meta.arg.workId;
      if (!workId) return;
      const entry = ensureEntry(state, workId);
      const pending = entry.pending[action.meta.requestId];
      if (pending) {
        entry.matches = removeMatchById(entry.matches, pending.tempId);
        delete entry.pending[action.meta.requestId];
      }
      entry.submitError = coerceErrorMessage(action.payload?.error ?? action.error, DEFAULT_SUBMIT_ERROR);
    });
  },
});

export const youtubeMatchesActions = youtubeMatchesSlice.actions;
export default youtubeMatchesSlice.reducer;

const selectYoutubeMatchesState = (state: RootState): YoutubeMatchesState =>
  state.youtubeMatches ?? initialState;

export const selectYoutubeMatchesEntry = (workId: string) => (state: RootState): YoutubeMatchesEntry | undefined =>
  selectYoutubeMatchesState(state).byWorkId[workId];

export const selectYoutubeMatches = (workId: string) => (state: RootState): WorkYoutubeMatch[] =>
  selectYoutubeMatchesEntry(workId)(state)?.matches ?? [];

export const selectYoutubeMatchesStatus = (workId: string) => (state: RootState): YoutubeMatchesStatus =>
  selectYoutubeMatchesEntry(workId)(state)?.status ?? 'idle';

export const selectYoutubeMatchesTotal = (workId: string) => (state: RootState): number =>
  selectYoutubeMatchesEntry(workId)(state)?.total ?? 0;

export const selectYoutubeMatchesError = (workId: string) => (state: RootState): string | undefined =>
  selectYoutubeMatchesEntry(workId)(state)?.error;

export const selectYoutubeMatchSubmitError = (workId: string) => (state: RootState): string | undefined =>
  selectYoutubeMatchesEntry(workId)(state)?.submitError;

export const selectYoutubeMatchIsSubmitting = (workId: string) => (state: RootState): boolean => {
  const entry = selectYoutubeMatchesEntry(workId)(state);
  if (!entry) return false;
  return Object.keys(entry.pending).length > 0;
};
